import { useAuth } from "@perlab/auth-bridge";
import { Button, Input, Modal } from "@perlab/ui";
import { useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useEffect, useId, useRef, useState, type ReactElement } from "react";
import { createPortal } from "react-dom";
import { useContextChain, type Workspace } from "../state/ContextChain";
import { navTo } from "./navTargets";

const MAX_WORKSPACE_NAME_LENGTH = 256;

/** Workspace creation is a platform-admin action, independent of the current node. */
export function WorkspaceCreateButton(): ReactElement | null {
  const { user, hasRole } = useAuth();
  if (!hasRole("Admin") && !hasRole("SuperAdmin")) return null;
  // An actor change or role loss unmounts the old form and invalidates its local completion effects.
  return <AuthorizedWorkspaceCreateButton key={user?.userId} />;
}

function AuthorizedWorkspaceCreateButton(): ReactElement {
  const { hasRole } = useAuth();
  const { addWorkspace } = useContextChain();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(false);
  const submitting = useRef(false);
  const formId = useId();
  const descriptionId = useId();
  const trimmedName = name.trim();
  const validName = trimmedName.length > 0 && trimmedName.length <= MAX_WORKSPACE_NAME_LENGTH;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const close = (): void => {
    if (submitting.current) return;
    setOpen(false);
    setName("");
    setError(null);
  };

  const submit = async (): Promise<void> => {
    if (!mounted.current || submitting.current || !validName || !open) return;
    if (!hasRole("Admin") && !hasRole("SuperAdmin")) return;
    submitting.current = true;
    setPending(true);
    setError(null);
    let workspace: Workspace;
    try {
      workspace = await addWorkspace({ label: trimmedName });
    } catch (cause) {
      if (mounted.current) {
        setError(cause instanceof Error ? cause.message : "Could not create the workspace. Please try again.");
      }
      return;
    } finally {
      submitting.current = false;
      if (mounted.current) setPending(false);
    }
    if (!mounted.current || (!hasRole("Admin") && !hasRole("SuperAdmin"))) return;
    setOpen(false);
    setName("");
    void navigate(navTo.workspaceHome(workspace.id));
  };

  return (
    <>
      <button
        type="button"
        aria-label="Add workspace"
        title="Add workspace"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
      >
        <Plus size={16} aria-hidden />
      </button>
      {createPortal(
        <Modal
          open={open}
          onClose={close}
          title="Add workspace"
          ariaDescribedBy={descriptionId}
          dismissible={!pending}
          footer={
            <>
              <Button type="button" variant="secondary" size="sm" onClick={close} disabled={pending}>
                Cancel
              </Button>
              <Button type="submit" form={formId} size="sm" disabled={pending || !validName} aria-busy={pending}>
                {pending ? "Creating workspace…" : "Create workspace"}
              </Button>
            </>
          }
        >
          <form id={formId} onSubmit={(event) => { event.preventDefault(); void submit(); }} className="flex flex-col gap-3">
            <p id={descriptionId} className="text-sm text-[var(--color-text-secondary)]">
              Create a workspace for clients, engagements, and projects.
            </p>
            <Input
              label="Workspace name"
              value={name}
              onChange={(event) => { setName(event.target.value); setError(null); }}
              maxLength={MAX_WORKSPACE_NAME_LENGTH}
              required
              disabled={pending}
              autoFocus
            />
            {pending && <p role="status">Creating workspace. Please wait.</p>}
            {error && <p role="alert" className="text-[var(--color-danger-500)]">{error}</p>}
          </form>
        </Modal>,
        document.body,
      )}
    </>
  );
}
